import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  useTenantStore,
  useTenantTimeZone,
  useTenantDateFormat,
  useTenantTimeFormat,
} from '../../../stores/tenant.store';
import { formatInstant } from '@dosfilos/shared';
import { audienceLabel } from './audience-labels';
import { callable } from '../../../lib/callable';

interface CampaignRecord {
  id: string;
  subject: string;
  status: string;
  audienceType: string;
  audienceSegment: any;
  scheduledAt: string | null;
  sentAt: string | null;
  sentCount: number;
  failedCount: number;
  updatedAt: string | null;
  createdAt: string | null;
}

const listCampaignsFn = callable('listCampaigns');
const cancelCampaignFn = callable('cancelCampaign');

export default function ScheduledList() {
  const { currentTenantId: tenantId } = useTenantStore();
  const tenantTz = useTenantTimeZone();
  const tenantFmt = useTenantDateFormat();
  const tenantTimeFmt = useTenantTimeFormat();
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const { t } = useTranslation('sis');
  const [toCancel, setToCancel] = useState<CampaignRecord | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listCampaignsFn({ tenantId, statuses: ['scheduled'] });
      const data = res.data as { items: CampaignRecord[] };
      setCampaigns(data.items);
    } catch (e: any) {
      setError(e.message ?? t('campaign.scheduledList.loadError'));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async (id: string) => {
    if (!tenantId) return;
    setCancelling(id);
    try {
      await cancelCampaignFn({ tenantId, campaignId: id });
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
    } catch (e: any) {
      setError(e.message ?? t('campaign.scheduledList.cancelError'));
    } finally {
      setCancelling(null);
    }
  };

  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {campaigns.length === 0
            ? t('campaign.scheduledList.empty')
            : t('campaign.scheduledList.count', { count: campaigns.length })}
        </Typography>
        <Tooltip title={t('campaign.tabs.refreshList')}>
          <IconButton size="small" onClick={load}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {campaigns.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <ScheduleIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body1">{t('campaign.scheduledList.empty')}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('campaign.scheduledList.emptyHint')}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{t('campaign.tabs.subject')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('campaign.tabs.audience')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('campaign.scheduledList.sendAt')}</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{t('campaign.tabs.actions')}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {c.subject}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={audienceLabel(c.audienceType)} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {c.scheduledAt ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ScheduleIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        <Typography variant="body2">
                          {formatInstant(c.scheduledAt, tenantTz, tenantFmt, tenantTimeFmt)}
                        </Typography>
                        {new Date(c.scheduledAt) < new Date() && (
                          <Chip
                            size="small"
                            label={t('campaign.scheduledList.processing')}
                            color="warning"
                          />
                        )}
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('campaign.scheduledList.cancel')}>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={cancelling === c.id}
                        onClick={() => setToCancel(c)}
                      >
                        {cancelling === c.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <CancelIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={toCancel !== null} onClose={() => setToCancel(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('campaign.editor.cancelSendTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('campaign.editor.cancelSendBody', { subject: toCancel?.subject ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToCancel(null)}>{t('campaign.editor.cancelSendBack')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const id = toCancel?.id;
              setToCancel(null);
              if (id) void handleCancel(id);
            }}
          >
            {t('campaign.editor.cancelSendConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
